/**
 * Token Manager
 * Tracks token expiry, schedules proactive refresh, and provides session warnings
 */ const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry
const WARNING_BUFFER_MS = 30 * 1000; // Show countdown banner 30s before session ends
const WARNING_DELAY_MS = 0; // No delay — show banner immediately at 30s
const STORAGE_KEY = 'auth_token_expiry';
let tokenExpiryTime = null;
let refreshTimeoutId = null;
let warningTimeoutId = null;
let expiryTimeoutId = null;
let onRefreshNeeded = null;
let onSessionWarning = null;
let onSessionExpired = null;
/**
 * Set callbacks for session events
 */ export function setSessionCallbacks(callbacks) {
    onRefreshNeeded = callbacks.onRefreshNeeded ?? null;
    onSessionWarning = callbacks.onSessionWarning ?? null;
    onSessionExpired = callbacks.onSessionExpired ?? null;
}
/**
 * Set the token expiry time and schedule refresh/warning timers
 * @param expiresIn - Time in seconds until token expires
 */ export function setTokenExpiry(expiresIn) {
    cancelScheduledTimers();
    const now = Date.now();
    tokenExpiryTime = now + expiresIn * 1000;
    // Persist expiry time for page refresh scenarios
    try {
        localStorage.setItem(STORAGE_KEY, String(tokenExpiryTime));
    } catch  {
    // localStorage may be unavailable
    }
    scheduleTimers();
}
/**
 * Restore token expiry from localStorage (called on mount)
 */ export function restoreTokenExpiry() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const expiry = Number.parseInt(stored, 10);
            if (!Number.isNaN(expiry)) {
                if (expiry > Date.now()) {
                    // Token is still valid - restore it and schedule timers
                    tokenExpiryTime = expiry;
                    scheduleTimers();
                } else {
                    // Token has already expired (from a previous session) — clean up silently.
                    // Do NOT trigger onSessionExpired here - that should only fire for tokens
                    // that expire during the current browser session, not stale tokens from storage.
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        }
    } catch  {
    // localStorage may be unavailable
    }
}
/**
 * Schedule proactive refresh, warning, and expiry timers
 */ function scheduleTimers() {
    if (!tokenExpiryTime) return;
    const now = Date.now();
    const timeUntilExpiry = tokenExpiryTime - now;
    // Schedule proactive refresh (5 min before expiry)
    const timeUntilRefresh = timeUntilExpiry - REFRESH_BUFFER_MS;
    if (timeUntilRefresh > 0 && onRefreshNeeded) {
        refreshTimeoutId = setTimeout(()=>{
            onRefreshNeeded?.();
        }, timeUntilRefresh);
    }
    // Schedule session warning slightly after the refresh attempt.
    // If proactive refresh succeeds, setTokenExpiry() → cancelScheduledTimers() cancels this.
    // If refresh fails, the warning fires after the delay.
    const timeUntilWarning = timeUntilExpiry - WARNING_BUFFER_MS + WARNING_DELAY_MS;
    if (timeUntilWarning >= 0 && onSessionWarning) {
        warningTimeoutId = setTimeout(()=>{
            onSessionWarning?.();
        }, timeUntilWarning);
    }
    // Schedule session expired (or fire immediately if already expired)
    if (onSessionExpired) {
        if (timeUntilExpiry > 0) {
            expiryTimeoutId = setTimeout(()=>{
                onSessionExpired?.();
            }, timeUntilExpiry);
        } else {
            onSessionExpired();
        }
    }
}
/**
 * Cancel any scheduled refresh/warning/expiry timers without clearing the
 * tracked expiry time. Callers must invoke this synchronously before any
 * awaited refresh call — otherwise the absolute expiry timer can fire during
 * the network round-trip and force a logout even when the refresh succeeds.
 */ export function cancelScheduledTimers() {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }
    if (warningTimeoutId) {
        clearTimeout(warningTimeoutId);
        warningTimeoutId = null;
    }
    if (expiryTimeoutId) {
        clearTimeout(expiryTimeoutId);
        expiryTimeoutId = null;
    }
}
/**
 * Get the current token expiry time
 */ export function getTokenExpiryTime() {
    return tokenExpiryTime;
}
/**
 * Check if the token is expired.
 * Returns false when no expiry is tracked (unknown state) — the server is the source of truth.
 */ export function isTokenExpired() {
    if (!tokenExpiryTime) return false;
    return Date.now() >= tokenExpiryTime;
}
/**
 * Check if the token needs refresh (within buffer period)
 */ export function needsRefresh() {
    if (!tokenExpiryTime) return false;
    return Date.now() >= tokenExpiryTime - REFRESH_BUFFER_MS;
}
/**
 * Clear token expiry state (called on logout)
 */ export function clearTokenExpiry() {
    cancelScheduledTimers();
    tokenExpiryTime = null;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch  {
    // localStorage may be unavailable
    }
}
/**
 * Get time remaining until expiry in milliseconds
 */ export function getTimeRemaining() {
    if (!tokenExpiryTime) return 0;
    return Math.max(0, tokenExpiryTime - Date.now());
}
